import {
  AUTH_SESSION_KEYS,
  SESSION_TYPES,
  SESSION_INDEX_KEY,
  SSO_STORAGE_KEYS,
  PKCE_STORAGE_KEYS,
  STORAGE_KEYS,
  MIGRATION_KEYS,
} from './sessionConstants';

export const buildPrefix = (type, userId, resourceId = null) => {
  if (!userId) return '';
  try {
    const byType = {
      [SESSION_TYPES.AUTOMY]: `automy_${userId}_`,
      [SESSION_TYPES.PORTAL]: `portal_${userId}_${resourceId}_`,
      [SESSION_TYPES.APP]: `app_${userId}_${resourceId}_`,
    };
    return byType[type] ?? '';
  } catch (e) {
    return '';
  }
};

/**
 * Parses a sessionId string back into its session context components.
 * Inverse of buildSessionId — used when only the sessionId is known (e.g. inside
 * the token refresh callback) and the caller needs to target the correct storage namespace.
 */
export const sessionIdToContext = (sessionId) => {
  if (!sessionId) return null;
  try {
    if (sessionId.startsWith('automy_')) {
      const userId = sessionId.slice('automy_'.length);
      return userId ? { type: SESSION_TYPES.AUTOMY, userId, resourceId: null } : null;
    }

    if (sessionId.startsWith('portal_')) {
      const middle = sessionId.slice('portal_'.length);
      const idx = middle.indexOf('_');
      if (idx === -1) return null;
      return {
        type: SESSION_TYPES.PORTAL,
        userId: middle.slice(0, idx),
        resourceId: middle.slice(idx + 1),
      };
    }

    if (sessionId.startsWith('app_')) {
      const middle = sessionId.slice('app_'.length);
      const idx = middle.indexOf('_');
      if (idx === -1) return null;
      return {
        type: SESSION_TYPES.APP,
        userId: middle.slice(0, idx),
        resourceId: middle.slice(idx + 1),
      };
    }

    return null;
  } catch (e) {
    return null;
  }
};

export const buildSessionId = (type, userId, resourceId = null) => {
  if (!userId) return null;
  try {
    const byType = {
      [SESSION_TYPES.AUTOMY]: `automy_${userId}`,
      [SESSION_TYPES.PORTAL]: `portal_${userId}_${resourceId}`,
      [SESSION_TYPES.APP]: `app_${userId}_${resourceId}`,
    };
    return byType[type] ?? null;
  } catch (e) {
    return null;
  }
};

const getPathname = () => {
  try {
    return window.location.pathname || '';
  } catch (e) {
    return '';
  }
};

/**
 * App id from the runtime viewer `/apps/:id` only.
 * Admin routes like `/apps/:id/builder` stay on the AUTOMY token — they are
 * not the public/self-managed app shell.
 */
export const getAppIdFromPath = () => {
  const match = getPathname().match(/^\/apps\/([^/]+)\/?$/);
  return match ? match[1] : null;
};

export const isAppPath = () => !!getAppIdFromPath();

/**
 * Session that owns the Bearer on this tab. On `/apps/:id` getSessionToken()
 * already reads the APP JWT while the index may still be AUTOMY (mint in
 * flight). Refreshing that JWT with Automy headers writes it into the Automy
 * slot and the next mint 401s.
 */
export const resolveRefreshSession = (session = getCurrentSession()) => {
  const pathAppId = getAppIdFromPath();
  if (pathAppId && session?.type !== SESSION_TYPES.APP) {
    const userId = session?.userId || findAppSessionUserId(pathAppId) || findAutomyUserId();
    if (userId) {
      return { type: SESSION_TYPES.APP, userId, resourceId: String(pathAppId) };
    }
  }
  return session;
};

export const isPortalPath = () => /^\/p(\/|$)/.test(getPathname());

/**
 * Surfaces where an APP index may stay active after login, before `/apps/:id`.
 * A leftover handoff key on `/` (Automy home) must not keep APP roles/token.
 */
export const isAppHandoffSurface = () => {
  const path = getPathname();
  if (path.startsWith('/user/login')) return true;
  try {
    const search = window.location.search || '';
    if (search.includes('code=') && (path === '/' || path === '')) return true;
  } catch (e) {}
  return false;
};

/** Google/Microsoft landing (`/?code=` or `#id_token=`). Not bare `/user/login`. */
const isOAuthCallbackSurface = () => {
  try {
    const search = window.location.search || '';
    if (search.includes('code=')) return true;
    if ((window.location.hash || '').includes('id_token=')) return true;
  } catch (e) {}
  return false;
};

/**
 * App and portal auth headers must never travel together. `?app=` / app
 * handoff wins over a leftover portal id from DVA or sessionStorage.
 */
const readPendingPortalId = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEYS.PORTAL_CONFIG);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.id ? String(parsed.id) : null;
  } catch (e) {
    return null;
  }
};

const isPortalLoginSurface = () => {
  if (isPortalPath()) return true;
  try {
    return !!new URLSearchParams(window.location.search || '').get('portal');
  } catch (e) {
    return false;
  }
};

/** True when this tab is actually in a portal (route, ?portal=, or PORTAL index). */
export const isActivePortalContext = (session = getCurrentSession()) => {
  if (isPortalLoginSurface()) return true;
  return session?.type === SESSION_TYPES.PORTAL;
};

/** Unprefixed config so `/user/login?portal=` can brand after logout. */
export const stashPortalLoginConfig = (portal) => {
  if (!portal || typeof portal !== 'object') return false;
  if (!portal.id && !portal.companyCode) return false;
  try {
    sessionStorage.setItem(STORAGE_KEYS.PORTAL_CONFIG, JSON.stringify(portal));
    return true;
  } catch (e) {
    return false;
  }
};

/**
 * Portal config for branding / ?portal= links. Leftover `config` in
 * sessionStorage after a failed /p/ visit must not brand Automy 2FA/login.
 */
export const getActivePortalConfig = () => {
  if (!isActivePortalContext()) return null;
  try {
    const raw = getSessionItem(STORAGE_KEYS.PORTAL_CONFIG);
    if (!raw) return null;
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return parsed?.id ? parsed : null;
  } catch (e) {
    return null;
  }
};

/**
 * Where a freshly minted JWT must be stored. A leftover unprefixed
 * PORTAL_CONFIG in sessionStorage (logged-out /p visit) must not send an
 * Automy password login into the PORTAL namespace.
 */
export const resolvePostLoginSession = ({ userId, isPortal = false, portalId = null } = {}) => {
  if (!userId) return null;

  const stored = getCurrentSession();
  let queryPortalId = null;
  try {
    queryPortalId = new URLSearchParams(window.location.search || '').get('portal');
  } catch (e) {}

  // URL / explicit id win. Leftover unprefixed PORTAL_CONFIG must not steal a
  // PORTAL refresh into another portal, and must not send Automy login to PORTAL.
  const pendingPortalId =
    portalId ||
    queryPortalId ||
    ((isPortal || isPortalLoginSurface()) && stored?.type !== SESSION_TYPES.PORTAL
      ? readPendingPortalId()
      : null);

  // Portal → Automy on `/p/:company/default` is still a portal path. Reusing
  // the PORTAL index here writes the Automy JWT into PORTAL; clearSession then
  // deletes it and SecurityLayout bounces to login.
  if (!isPortal && !pendingPortalId) {
    return { type: SESSION_TYPES.AUTOMY, userId, resourceId: null };
  }

  if (
    stored &&
    String(stored.userId) === String(userId) &&
    stored.type === SESSION_TYPES.PORTAL &&
    (!pendingPortalId || String(stored.resourceId) === String(pendingPortalId))
  ) {
    return stored;
  }

  if (pendingPortalId) {
    return { type: SESSION_TYPES.PORTAL, userId, resourceId: String(pendingPortalId) };
  }

  if (
    stored &&
    String(stored.userId) === String(userId) &&
    stored.type === SESSION_TYPES.AUTOMY &&
    !isPortal &&
    !isPortalLoginSurface()
  ) {
    return stored;
  }

  return { type: SESSION_TYPES.AUTOMY, userId, resourceId: null };
};

export const persistPortalOAuthPending = (portalId) => {
  if (!portalId) return;
  setGlobalItem(STORAGE_KEYS.PORTAL_OAUTH_PENDING, String(portalId));
};

export const clearPortalLoginHandoff = () => {
  try {
    sessionStorage.removeItem(STORAGE_KEYS.PORTAL_LOGIN_HANDOFF);
  } catch (e) {}
  removeGlobalItem(STORAGE_KEYS.PORTAL_OAUTH_PENDING);
};

/** `?portal=` now, or PORTAL_OAUTH_PENDING only on the Google/Microsoft callback. */
export const readPendingPortalLoginId = () => {
  try {
    const query = new URLSearchParams(window.location.search || '').get('portal');
    if (query) return String(query);
  } catch (e) {}
  try {
    const pending = getGlobalItem(STORAGE_KEYS.PORTAL_OAUTH_PENDING);
    if (pending && isOAuthCallbackSurface()) return String(pending);
  } catch (e) {}
  return null;
};

export const resolveAuthContextHeaders = ({ portalId, appId } = {}) => {
  try {
    const params = new URLSearchParams(window.location.search || '');
    const queryApp = params.get('app');
    const queryPortal = params.get('portal');
    const leftoverApp = getGlobalItem(STORAGE_KEYS.APP_LOGIN_CONTEXT);
    const candidateApp = queryApp || appId || leftoverApp;
    const explicitApp = shouldApplyAppLoginContext(candidateApp) ? candidateApp : queryApp;
    const pendingPortal = readPendingPortalLoginId();
    // Caller/DVA leftovers must not mint Automy logins as portal. Only a
    // real portal surface (?portal=, /p/, PORTAL index, or OAuth pending) may.
    const explicitPortal =
      queryPortal || pendingPortal || (isActivePortalContext() ? portalId || queryPortal : null);

    if (explicitApp && !explicitPortal) return { appId: String(explicitApp) };
    if (explicitPortal && !explicitApp) return { portalId: String(explicitPortal) };
    if (explicitApp && explicitPortal) return { appId: String(explicitApp) };
  } catch (e) {}
  return {};
};

/**
 * True while an app login is in flight: token is already in the APP namespace
 * but the tab is still on `/user/login` or the OAuth callback (`/`).
 * Without this, getCurrentSession() would revert to AUTOMY and getSessionToken()
 * would return null — the next 401 then wipes the brand-new APP token.
 */
/**
 * Leftover APP_LOGIN_CONTEXT is only a real app login when the tab is on
 * `/apps/:id`, `?app=` is present, or APP_LOGIN_HANDOFF is set. Otherwise
 * Automy OAuth on `/` (or `/?code=`) would write the JWT into APP.
 */
export const shouldApplyAppLoginContext = (
  appId = getGlobalItem(STORAGE_KEYS.APP_LOGIN_CONTEXT),
) => {
  if (!appId) return false;
  if (isAppPath()) return true;
  try {
    if (new URLSearchParams(window.location.search || '').get('app')) return true;
  } catch (e) {}
  if (isPendingAppHandoff(appId)) return true;
  // Google/Microsoft leave /user/login?app= and return on /?code= without
  // query params. APP_OAUTH_PENDING is set only when that OAuth started
  // from an app login — leftover CONTEXT alone must not steal Automy OAuth.
  try {
    const pending = getGlobalItem(STORAGE_KEYS.APP_OAUTH_PENDING);
    // Only the provider callback. Bare `/user/login` after a cancelled OAuth
    // must not write an Automy password into the APP namespace.
    if (pending && String(pending) === String(appId) && isOAuthCallbackSurface()) return true;
  } catch (e) {}
  return false;
};

export const isPendingAppHandoff = (resourceId) => {
  if (!resourceId) return false;
  try {
    const handoff = localStorage.getItem(STORAGE_KEYS.APP_LOGIN_HANDOFF);
    return !!handoff && String(handoff) === String(resourceId);
  } catch (e) {
    return false;
  }
};

export const clearAppLoginHandoff = () => {
  try {
    localStorage.removeItem(STORAGE_KEYS.APP_LOGIN_HANDOFF);
  } catch (e) {}
};

/** True only while an automatic SSO handoff is in flight — leftover URL alone is not. */
export const isActiveSsoHandoff = () => {
  try {
    return (
      localStorage.getItem(STORAGE_KEYS.SSO_AUTOMATIC_FLOW) === 'true' &&
      !!localStorage.getItem(STORAGE_KEYS.SSO_REDIRECT_URL)
    );
  } catch (e) {
    return false;
  }
};

/** Drop leftover SSO client flags. Does not touch AUTOMY / PORTAL / APP tokens. */
export const clearSsoLoginFlags = () => {
  try {
    SSO_STORAGE_KEYS.forEach((key) => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });
    PKCE_STORAGE_KEYS.forEach((key) => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });
  } catch (e) {}
};

/** Drop leftover app-login flags. Does not touch AUTOMY / PORTAL tokens. */
export const clearAppLoginFlags = () => {
  try {
    localStorage.removeItem(STORAGE_KEYS.APP_LOGIN_CONTEXT);
    localStorage.removeItem(STORAGE_KEYS.APP_LOGIN_HANDOFF);
    localStorage.removeItem(STORAGE_KEYS.APP_REDIRECT_AFTER_LOGIN);
    localStorage.removeItem(STORAGE_KEYS.APP_OAUTH_PENDING);
  } catch (e) {}
};

/** Drop the APP namespace for this app. Used when the user logs out of /apps/:id. */
export const dropAppSessionOnLogout = (appId) => {
  try {
    if (!appId) return;
    let storedSession = null;
    try {
      const raw = sessionStorage.getItem(SESSION_INDEX_KEY);
      storedSession = raw ? JSON.parse(raw) : null;
    } catch (e) {}
    const userId = storedSession?.userId || findAppSessionUserId(appId);
    if (userId) clearSession(SESSION_TYPES.APP, userId, String(appId));
    clearAppLoginFlags();
  } catch (e) {}
};

/** User id that owns `app_{userId}_{appId}_auth-token`. */
export const findAppSessionUserId = (appId) => {
  if (!appId) return null;
  try {
    const needle = `_${appId}_${STORAGE_KEYS.AUTH_TOKEN}`;
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith('app_') || !key.endsWith(needle)) continue;
      const userId = key.slice('app_'.length, key.length - needle.length);
      if (userId) return userId;
    }
  } catch (e) {}
  return null;
};

/**
 * Newest APP token for `ownerUserId` (not a self-managed public app).
 * Requires an owner so a leftover token on a shared PC cannot mint a session
 * for someone else.
 */
export const findExchangeableAppCredential = (ownerUserId = null) => {
  if (ownerUserId == null || ownerUserId === '') return null;

  try {
    const owner = String(ownerUserId);
    const suffix = `_${STORAGE_KEYS.AUTH_TOKEN}`;
    let best = null;
    let bestIssuedAt = -1;

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith('app_') || !key.endsWith(suffix)) continue;

      const middle = key.slice('app_'.length, key.length - suffix.length);
      const split = middle.lastIndexOf('_');
      if (split <= 0) continue;

      const userId = middle.slice(0, split);
      const appId = middle.slice(split + 1);
      if (!userId || !appId || userId === appId) continue;
      if (userId !== owner) continue;

      const token = parseStoredToken(localStorage.getItem(key));
      if (!isStoredTokenFresh(token)) continue;

      const issuedAt = token.issuedAt || 0;
      if (issuedAt >= bestIssuedAt) {
        best = { userId, appId, token };
        bestIssuedAt = issuedAt;
      }
    }

    return best;
  } catch (e) {
    return null;
  }
};

export const hasUsableAppToken = (appId) => {
  try {
    if (!appId) return false;
    let storedSession = null;
    try {
      const raw = sessionStorage.getItem(SESSION_INDEX_KEY);
      storedSession = raw ? JSON.parse(raw) : null;
    } catch (e) {}

    const candidates = [storedSession?.userId, findAppSessionUserId(appId)].filter(Boolean);
    const seen = new Set();
    for (const userId of candidates) {
      const id = String(userId);
      if (seen.has(id)) continue;
      seen.add(id);
      if (
        isStoredTokenFresh(getItem(SESSION_TYPES.APP, id, String(appId), STORAGE_KEYS.AUTH_TOKEN))
      ) {
        return true;
      }
    }
    return false;
  } catch (e) {
    return false;
  }
};

/** Self-managed / public apps store the token as `app_{appId}_{appId}_`. */
export const isSelfManagedAppSession = (session) =>
  session?.type === SESSION_TYPES.APP &&
  !!session.userId &&
  String(session.userId) === String(session.resourceId);

const parseStoredToken = (raw) => {
  if (!raw) return null;
  try {
    const data = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return data?.accessToken ? data : null;
  } catch (e) {
    return null;
  }
};

const SKEW_MS = 30_000;

/** True when issuedAt/expiresIn and JWT `exp` (if present) are still in the future. */
export const isStoredTokenFresh = (raw) => {
  const token = parseStoredToken(raw);
  if (!token?.accessToken) return false;

  if (token.issuedAt && token.expiresIn) {
    if (Date.now() >= token.issuedAt + token.expiresIn * 1000 - SKEW_MS) return false;
  }

  try {
    const parts = String(token.accessToken).split('.');
    if (parts.length === 3) {
      const json = typeof atob === 'function' ? atob(parts[1]) : null;
      if (json) {
        const payload = JSON.parse(json);
        if (payload.exp && Date.now() >= payload.exp * 1000 - SKEW_MS) return false;
      }
    }
  } catch (e) {}

  return true;
};

/**
 * True when storage now holds a different, still valid token than the one a
 * refresh was sent with. A company/environment switch or another tab's refresh
 * replaced it while the request was out; that token is the newer one, and the
 * refresh outcome — success or a rejected refresh token — must not override it.
 */
export const isSupersededByStoredToken = (sentToken, storedToken) => {
  const sent = parseStoredToken(sentToken);
  const stored = parseStoredToken(storedToken);
  if (!sent || !stored || sent.accessToken === stored.accessToken) return false;
  return isStoredTokenFresh(stored);
};

export const readRolesFromAccessToken = (accessToken) => {
  try {
    const parts = String(accessToken || '').split('.');
    if (parts.length !== 3) return null;
    const json = typeof atob === 'function' ? atob(parts[1]) : null;
    if (!json) return null;
    const payload = JSON.parse(json);
    if (payload?.roles == null) return null;
    return Array.isArray(payload.roles) ? payload.roles : JSON.parse(payload.roles);
  } catch (e) {
    return null;
  }
};

export const persistAuthorityForSession = (type, userId, resourceId, roles) => {
  if (!type || !userId || roles == null) return;
  const normalized = typeof roles === 'string' ? [roles] : roles;
  if (!Array.isArray(normalized)) return;
  setItem(type, userId, resourceId, STORAGE_KEYS.AUTHORITY, JSON.stringify(normalized));
};

const restoreAutomyAuthority = (automy) => {
  if (!automy?.userId) return;
  const token = parseStoredToken(
    getItem(SESSION_TYPES.AUTOMY, automy.userId, null, STORAGE_KEYS.AUTH_TOKEN),
  );
  const roles = readRolesFromAccessToken(token?.accessToken);
  if (roles) persistAuthorityForSession(SESSION_TYPES.AUTOMY, automy.userId, null, roles);
};

const isAutomyTokenCopiedFromPrefix = (userId, prefix) => {
  if (!userId) return false;
  const automy = parseStoredToken(
    getItem(SESSION_TYPES.AUTOMY, userId, null, STORAGE_KEYS.AUTH_TOKEN),
  );
  if (!automy?.accessToken) return false;

  const suffix = `_${STORAGE_KEYS.AUTH_TOKEN}`;
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(prefix) || !key.endsWith(suffix)) continue;
      const other = parseStoredToken(localStorage.getItem(key));
      if (other?.accessToken && other.accessToken === automy.accessToken) return true;
    }
  } catch (e) {}
  return false;
};

/** True when the AUTOMY slot holds the same JWT as one of this user's APP tokens. */
export const isAutomyTokenCopiedFromApp = (userId) =>
  isAutomyTokenCopiedFromPrefix(userId, `app_${userId}_`);

/** True when the AUTOMY slot holds a portal-bound JWT copied from PORTAL. */
export const isAutomyTokenCopiedFromPortal = (userId) =>
  isAutomyTokenCopiedFromPrefix(userId, `portal_${userId}_`);

/**
 * Reuse a leftover APP token only when it is still fresh AND was minted at or
 * after the current AUTOMY token. After an Automy refresh the APP JWT is often
 * already dead on the server; skipping `/auth/automy` + appId then bounces
 * the user to login.
 */
export const shouldReuseExistingAppToken = (userId, appId) => {
  if (!userId || !appId) return false;
  const appRaw = getItem(SESSION_TYPES.APP, userId, String(appId), STORAGE_KEYS.AUTH_TOKEN);
  if (!isStoredTokenFresh(appRaw)) return false;

  const automy = parseStoredToken(
    getItem(SESSION_TYPES.AUTOMY, userId, null, STORAGE_KEYS.AUTH_TOKEN),
  );
  if (!automy?.accessToken) return true;

  const app = parseStoredToken(appRaw);
  if ((automy.issuedAt || 0) > (app.issuedAt || 0) + 1_000) return false;
  return true;
};

export const hasUsableAutomyToken = (userId) => {
  if (!userId) return false;
  if (isAutomyTokenCopiedFromApp(userId) || isAutomyTokenCopiedFromPortal(userId)) return false;
  return isStoredTokenFresh(getItem(SESSION_TYPES.AUTOMY, userId, null, STORAGE_KEYS.AUTH_TOKEN));
};

/**
 * On `/apps/:id` a 401 can arrive before the APP JWT is written. Wiping every
 * namespace then would destroy the Automy session the mint still needs.
 */
export const shouldPreserveAutomyOnMissingAppToken = () => {
  const appId = getAppIdFromPath();
  if (!appId) return false;
  const userId = getCurrentSession()?.userId || findAppSessionUserId(appId) || findAutomyUserId();
  return !!userId && hasUsableAutomyToken(userId);
};

const readSessionOwner = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.SESSION_OWNER);
    if (!raw) return null;
    const owner = JSON.parse(raw);
    if (!owner?.userId) return null;
    return {
      type: owner.type || SESSION_TYPES.AUTOMY,
      userId: String(owner.userId),
      resourceId: owner.resourceId ?? null,
    };
  } catch (e) {
    return null;
  }
};

const persistSessionOwner = (session) => {
  try {
    if (!session?.userId) {
      localStorage.removeItem(STORAGE_KEYS.SESSION_OWNER);
      return;
    }

    if (session.type === SESSION_TYPES.APP) {
      if (isSelfManagedAppSession(session)) return;
      localStorage.setItem(
        STORAGE_KEYS.SESSION_OWNER,
        JSON.stringify({
          type: SESSION_TYPES.AUTOMY,
          userId: String(session.userId),
          resourceId: null,
          at: Date.now(),
        }),
      );
      return;
    }

    localStorage.setItem(
      STORAGE_KEYS.SESSION_OWNER,
      JSON.stringify({
        type: session.type,
        userId: String(session.userId),
        resourceId: session.resourceId ?? null,
        at: Date.now(),
      }),
    );
  } catch (e) {}
};

const tokenHasAccess = (raw) => !!parseStoredToken(raw)?.accessToken;

/**
 * Pre-owner sessions (logged in before session-owner existed) still need
 * Ctrl+Click. Only infer when a single userId owns automy/portal tokens.
 */
const inferSoleOwner = () => {
  const suffix = `_${STORAGE_KEYS.AUTH_TOKEN}`;
  const byUser = new Map();

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key || !key.endsWith(suffix) || !tokenHasAccess(localStorage.getItem(key))) continue;

    let userId = null;
    let candidate = null;

    if (key.startsWith('automy_')) {
      userId = key.slice('automy_'.length, key.length - suffix.length);
      if (userId) candidate = { type: SESSION_TYPES.AUTOMY, userId, resourceId: null };
    } else if (key.startsWith('portal_')) {
      const middle = key.slice('portal_'.length, key.length - suffix.length);
      const split = middle.indexOf('_');
      if (split > 0) {
        userId = middle.slice(0, split);
        candidate = {
          type: SESSION_TYPES.PORTAL,
          userId,
          resourceId: middle.slice(split + 1),
        };
      }
    }

    if (!userId || !candidate) continue;
    const list = byUser.get(userId) || [];
    list.push(candidate);
    byUser.set(userId, list);
  }

  if (byUser.size !== 1) return null;
  const [userId, list] = byUser.entries().next().value;
  const portal = list.find((c) => c.type === SESSION_TYPES.PORTAL);
  return portal || { type: SESSION_TYPES.AUTOMY, userId, resourceId: null };
};

/**
 * Restore the session index in a new tab (empty sessionStorage).
 * Prefers session-owner; if missing, restores only when a single user has tokens.
 */
const tryRestoreSession = () => {
  try {
    if (sessionStorage.getItem(STORAGE_KEYS.LOGOUT_IN_PROGRESS)) return null;

    const owner = readSessionOwner() || inferSoleOwner();
    if (!owner?.userId) return null;

    if (owner.type === SESSION_TYPES.PORTAL && owner.resourceId) {
      if (
        tokenHasAccess(
          getItem(SESSION_TYPES.PORTAL, owner.userId, owner.resourceId, STORAGE_KEYS.AUTH_TOKEN),
        )
      ) {
        const session = {
          type: SESSION_TYPES.PORTAL,
          userId: owner.userId,
          resourceId: String(owner.resourceId),
        };
        setCurrentSession(session);
        return session;
      }
    }

    if (
      tokenHasAccess(getItem(SESSION_TYPES.AUTOMY, owner.userId, null, STORAGE_KEYS.AUTH_TOKEN))
    ) {
      const session = { type: SESSION_TYPES.AUTOMY, userId: owner.userId, resourceId: null };
      setCurrentSession(session);
      return session;
    }

    return null;
  } catch (e) {
    return null;
  }
};

export const getCurrentSession = () => {
  try {
    const raw = sessionStorage.getItem(SESSION_INDEX_KEY);
    const session = raw ? JSON.parse(raw) : tryRestoreSession();
    if (!session) return null;

    if (session.userId && !readSessionOwner()?.userId) persistSessionOwner(session);

    // An APP index is only valid on `/apps/:id`, except during the post-login
    // handoff (still on `/user/login` or `/?code=` with a fresh APP token).
    if (session.type === SESSION_TYPES.APP && !getAppIdFromPath()) {
      if (isPendingAppHandoff(session.resourceId) && isAppHandoffSurface()) return session;
      clearAppLoginHandoff();
      const automy = resolveAutomySession(session);
      setCurrentSession(automy);
      restoreAutomyAuthority(automy);
      return automy;
    }

    return session;
  } catch (e) {
    return null;
  }
};

export const setCurrentSession = (session) => {
  try {
    if (session === null || session === undefined) {
      sessionStorage.removeItem(SESSION_INDEX_KEY);
      persistSessionOwner(null);
      return;
    }
    sessionStorage.setItem(SESSION_INDEX_KEY, JSON.stringify(session));
    persistSessionOwner(session);
  } catch (e) {}
};

export const getCurrentPrefix = () => {
  try {
    const session = getCurrentSession();
    if (!session) return '';
    return buildPrefix(session.type, session.userId, session.resourceId);
  } catch (e) {
    return '';
  }
};

export const getCurrentSessionId = () => {
  try {
    const session = getCurrentSession();
    if (!session) return null;
    return buildSessionId(session.type, session.userId, session.resourceId);
  } catch (e) {
    return null;
  }
};

/** Session id that owns the Bearer this tab must refresh (APP on `/apps/:id`). */
export const getRefreshSessionId = (session = resolveRefreshSession()) => {
  try {
    if (!session?.userId) return null;
    return buildSessionId(session.type, session.userId, session.resourceId);
  } catch (e) {
    return null;
  }
};

/** Persist flags so a DVA refresh writes back into the session that was refreshed. */
export const buildRefreshLoginPayload = (session) => {
  if (session?.type === SESSION_TYPES.APP && session.resourceId) {
    return { _appLoginAppId: String(session.resourceId) };
  }
  return {
    _skipAppLogin: true,
    ...(session?.type === SESSION_TYPES.PORTAL ? { isPortal: true } : {}),
  };
};

/** Token stored in a specific namespace — never the leftover index on `/apps/:id`. */
export const readStoredSessionToken = (session) => {
  if (!session?.userId) return null;
  return parseStoredToken(
    getItem(session.type, session.userId, session.resourceId, STORAGE_KEYS.AUTH_TOKEN),
  );
};

export const getItem = (type, userId, resourceId, baseKey) => {
  try {
    const prefix = buildPrefix(type, userId, resourceId);
    return localStorage.getItem(prefix + baseKey);
  } catch (e) {
    return null;
  }
};

export const setItem = (type, userId, resourceId, baseKey, value) => {
  try {
    const prefix = buildPrefix(type, userId, resourceId);
    if (!prefix) return;
    else localStorage.setItem(prefix + baseKey, value);
  } catch (e) {}
};

export const removeItem = (type, userId, resourceId, baseKey) => {
  try {
    const prefix = buildPrefix(type, userId, resourceId);
    localStorage.removeItem(prefix + baseKey);
  } catch (e) {}
};

export const getSessionItem = (baseKey) => {
  try {
    const session = getCurrentSession();
    if (session) {
      const prefix = buildPrefix(session.type, session.userId, session.resourceId);
      if (prefix) return localStorage.getItem(prefix + baseKey);
    }
    return sessionStorage.getItem(baseKey);
  } catch (e) {
    return null;
  }
};

export const setSessionItem = (baseKey, value) => {
  try {
    const session = getCurrentSession();
    if (session) {
      const prefix = buildPrefix(session.type, session.userId, session.resourceId);
      if (prefix) {
        localStorage.setItem(prefix + baseKey, value);
        return;
      }
    }
    sessionStorage.setItem(baseKey, value);
  } catch (e) {}
};

export const removeSessionItem = (baseKey) => {
  try {
    const session = getCurrentSession();
    if (session) {
      const prefix = buildPrefix(session.type, session.userId, session.resourceId);
      if (prefix) {
        localStorage.removeItem(prefix + baseKey);
        return;
      }
    }
    sessionStorage.removeItem(baseKey);
  } catch (e) {}
};

const REFRESH_LOCK_MS = 30_000;

/** Headers for /auth/refreshToken so APP/PORTAL JWTs are never refreshed as Automy. */
export const buildRefreshContext = (session = getCurrentSession()) => {
  if (session?.type === SESSION_TYPES.APP && session.resourceId) {
    return { appId: session.resourceId, portal: null };
  }
  if (session?.type === SESSION_TYPES.PORTAL && session.userId && session.resourceId) {
    let portal = null;
    try {
      const raw = getItem(
        session.type,
        session.userId,
        session.resourceId,
        STORAGE_KEYS.PORTAL_CONFIG,
      );
      portal = raw ? JSON.parse(raw) : null;
    } catch (e) {}
    // resourceId is the portalId even when leftover config was wiped. Without
    // it, /auth/refreshToken would mint an Automy JWT into the PORTAL slot.
    return {
      appId: null,
      portal: {
        ...(portal && typeof portal === 'object' ? portal : {}),
        id: String(session.resourceId),
      },
    };
  }
  return { appId: null, portal: null };
};

export const getRefreshingStartedAt = (type, userId, resourceId) => {
  try {
    const raw = userId
      ? getItem(type, userId, resourceId, STORAGE_KEYS.AUTH_REFRESHING)
      : getSessionItem(STORAGE_KEYS.AUTH_REFRESHING);
    const started = raw ? parseInt(raw, 10) : 0;
    if (!started || Date.now() - started >= REFRESH_LOCK_MS) return 0;
    return started;
  } catch (e) {
    return 0;
  }
};

export const markSessionRefreshing = (type, userId, resourceId) => {
  const stamp = Date.now().toString();
  if (userId) setItem(type, userId, resourceId, STORAGE_KEYS.AUTH_REFRESHING, stamp);
  else setSessionItem(STORAGE_KEYS.AUTH_REFRESHING, stamp);
};

export const clearSessionRefreshing = (type, userId, resourceId) => {
  if (userId) removeItem(type, userId, resourceId, STORAGE_KEYS.AUTH_REFRESHING);
  else removeSessionItem(STORAGE_KEYS.AUTH_REFRESHING);
};

export const clearSession = (type, userId, resourceId) => {
  try {
    const prefix = buildPrefix(type, userId, resourceId);
    if (!prefix) return;

    const keysToRemove = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch (e) {}
};

/**
 * Drop the credential keys of every namespace (automy/portal/app) belonging to
 * a user, leaving non-auth data such as the portal branding config in place.
 *
 * Removing only the active namespace is not enough: a token surviving in another
 * namespace is resurrected later by tryRestoreSession, which puts the tab on a
 * session the shell cannot read.
 */
export const clearUserAuthTokens = (userId) => {
  if (!userId) return;
  try {
    const prefixes = [`automy_${userId}_`, `portal_${userId}_`, `app_${userId}_`];
    const keysToRemove = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      if (!prefixes.some((p) => key.startsWith(p))) continue;
      if (AUTH_SESSION_KEYS.some((authKey) => key.endsWith(authKey))) keysToRemove.push(key);
    }

    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch (e) {}
};

export const clearAllUserSessions = (userId) => {
  if (!userId) return;
  try {
    const prefixes = [`automy_${userId}_`, `portal_${userId}_`, `app_${userId}_`];
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && prefixes.some((p) => key.startsWith(p))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch (e) {}
};

const isAppCrossContext = (fromCtx, toCtx) => {
  const fromApp = fromCtx?.type === SESSION_TYPES.APP;
  const toApp = toCtx?.type === SESSION_TYPES.APP;
  return fromApp !== toApp;
};

/** JWTs and roles are audience-bound. Never copy them Automy ↔ portal. */
const isAudienceBoundKey = (key) =>
  key === STORAGE_KEYS.AUTH_TOKEN || key === STORAGE_KEYS.AUTHORITY;

const transferSessionKeys = (fromCtx, toCtx, keys, { removeSource }) => {
  if (isAppCrossContext(fromCtx, toCtx)) return;

  const fromPrefix = buildPrefix(fromCtx.type, fromCtx.userId, fromCtx.resourceId);
  const toPrefix = buildPrefix(toCtx.type, toCtx.userId, toCtx.resourceId);
  if (!fromPrefix || !toPrefix) return;

  const crossType = fromCtx.type !== toCtx.type;
  keys.forEach((key) => {
    if (crossType && isAudienceBoundKey(key)) return;
    try {
      const value = localStorage.getItem(fromPrefix + key);
      if (value !== null) {
        localStorage.setItem(toPrefix + key, value);
        if (removeSource) localStorage.removeItem(fromPrefix + key);
      }
    } catch (e) {}
  });
};

export const migrateSession = (fromCtx, toCtx, keys = MIGRATION_KEYS) => {
  try {
    transferSessionKeys(fromCtx, toCtx, keys, { removeSource: true });
  } catch (e) {}
};

export const copySession = (fromCtx, toCtx, keys = MIGRATION_KEYS) => {
  try {
    transferSessionKeys(fromCtx, toCtx, keys, { removeSource: false });
  } catch (e) {}
};

/**
 * Move the active session index from an APP context back to AUTOMY, which is
 * what every non `/apps/*` route expects.
 *
 * Tokens are never copied: an APP refresh token is bound to that app (and a
 * public/self-managed token is not the Automy user). Switching the index is
 * enough; each namespace keeps its own credentials.
 *
 * @returns {boolean} true when the session index was changed.
 */
export const revertAppSessionToAutomy = () => {
  try {
    const raw = sessionStorage.getItem(SESSION_INDEX_KEY);
    const session = raw ? JSON.parse(raw) : null;
    if (session?.type !== SESSION_TYPES.APP || !session.userId) return false;
    if (isPendingAppHandoff(session.resourceId) && isAppHandoffSurface()) return false;

    clearAppLoginHandoff();
    const automy = resolveAutomySession(session);
    setCurrentSession(automy);
    restoreAutomyAuthority(automy);
    return true;
  } catch (e) {
    return false;
  }
};

export const findAutomyUserId = () => {
  try {
    const owner = readSessionOwner() || inferSoleOwner();
    if (
      owner?.userId &&
      tokenHasAccess(getItem(SESSION_TYPES.AUTOMY, owner.userId, null, STORAGE_KEYS.AUTH_TOKEN))
    ) {
      return owner.userId;
    }
    return null;
  } catch (e) {
    return null;
  }
};

/**
 * AUTOMY session to use after leaving an APP context.
 * Public/self-managed apps reuse the appId as userId — that must never become
 * an AUTOMY index, or every Automy request would carry the public app token.
 */
export const resolveAutomySession = (appSession) => {
  if (!appSession?.userId) return null;
  if (isSelfManagedAppSession(appSession)) {
    const automyUserId = findAutomyUserId();
    return automyUserId
      ? { type: SESSION_TYPES.AUTOMY, userId: automyUserId, resourceId: null }
      : null;
  }
  return { type: SESSION_TYPES.AUTOMY, userId: appSession.userId, resourceId: null };
};

/**
 * Do not copy APP/PORTAL JWTs into AUTOMY. They are audience-bound; overwriting
 * the Automy slot makes the next /auth/automy + appId/portalId fail.
 */
export const syncRefreshedToken = () => {};

/**
 * Company/environment switch issues an Automy tenant JWT. Never write it into
 * APP or PORTAL — those Bearers are audience-bound.
 */
export const persistAutomyTenantToken = (tokenPayload) => {
  if (!tokenPayload?.accessToken) return false;
  const serialized = JSON.stringify({ ...tokenPayload, issuedAt: Date.now() });
  const session = getCurrentSession();
  const userId = session?.userId || findAutomyUserId();
  if (!userId) return false;
  setItem(SESSION_TYPES.AUTOMY, userId, null, STORAGE_KEYS.AUTH_TOKEN, serialized);
  return true;
};

export const getSessionToken = () => {
  try {
    const appId = getAppIdFromPath();

    // App routes: only that app's token. Never the AUTOMY/PORTAL Bearer.
    if (appId) {
      const session = getCurrentSession();
      if (session?.type === SESSION_TYPES.APP && String(session.resourceId) === String(appId)) {
        const token = parseStoredToken(
          getItem(SESSION_TYPES.APP, session.userId, session.resourceId, STORAGE_KEYS.AUTH_TOKEN),
        );
        if (token) return token;
      }

      const ownerId =
        session?.userId && !isSelfManagedAppSession(session) ? session.userId : findAutomyUserId();
      if (ownerId) {
        const owned = parseStoredToken(
          getItem(SESSION_TYPES.APP, ownerId, String(appId), STORAGE_KEYS.AUTH_TOKEN),
        );
        if (owned) return owned;
      }

      return parseStoredToken(
        getItem(SESSION_TYPES.APP, String(appId), String(appId), STORAGE_KEYS.AUTH_TOKEN),
      );
    }

    // Automy / portal routes: never an APP token — unless the user just signed
    // into an app and we have not navigated to `/apps/:id` yet.
    const session = getCurrentSession();
    if (!session) return null;
    if (session.type === SESSION_TYPES.APP) {
      if (isPendingAppHandoff(session.resourceId)) {
        return parseStoredToken(
          getItem(SESSION_TYPES.APP, session.userId, session.resourceId, STORAGE_KEYS.AUTH_TOKEN),
        );
      }
      return null;
    }

    return parseStoredToken(
      getItem(session.type, session.userId, session.resourceId, STORAGE_KEYS.AUTH_TOKEN),
    );
  } catch (e) {
    return null;
  }
};

// ---------------------------------------------------------------------------
// Non-session-scoped storage helpers
// ---------------------------------------------------------------------------
// "Global" items live in localStorage but are NOT prefixed by any session.
// Use for pre-authentication flags that must survive full-page redirects
// (e.g. OAuth round-trips) before a session exists.
// ---------------------------------------------------------------------------
export const getGlobalItem = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    return null;
  }
};

export const setGlobalItem = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch (e) {}
};

export const removeGlobalItem = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (e) {}
};

// "Tab" items live in sessionStorage — cleared when the tab closes.
// Use for ephemeral caches that should not leak across tabs.
export const getTabItem = (key) => {
  try {
    return sessionStorage.getItem(key);
  } catch (e) {
    return null;
  }
};

export const setTabItem = (key, value) => {
  try {
    sessionStorage.setItem(key, value);
  } catch (e) {}
};

export const removeTabItem = (key) => {
  try {
    sessionStorage.removeItem(key);
  } catch (e) {}
};
